import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Inbox, Check, MessageSquare, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useOutletContext } from 'react-router-dom';
import { devError } from '../utils/logger';
import ReplyDialog from '../components/ReplyDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.round(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
  } catch { return iso; }
};

export default function WingmanInboxPage() {
  const { refreshMe } = useOutletContext() || {};
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [marking, setMarking] = useState({});
  const [replyOpen, setReplyOpen] = useState(null);  // inquiry being replied to

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/supplier/pending-enquiries?limit=50`);
      setItems(data.enquiries || []);
    } catch (e) {
      devError('inbox load failed', e);
      toast.error("Couldn't load enquiries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markReplied = async (q) => {
    if (!q.inquiry_id) {
      toast.info("Older enquiry — reply by email.");
      return;
    }
    setMarking((m) => ({ ...m, [q.event_id]: true }));
    try {
      await axios.post(`${API}/supplier/inquiries/${q.inquiry_id}/mark-replied`);
      setItems((arr) => arr.filter((x) => x.event_id !== q.event_id));
      refreshMe?.();
      toast.success('Marked as replied');
    } catch (e) {
      devError('mark-replied failed', e);
      toast.error("Couldn't mark as replied");
    } finally {
      setMarking((m) => {
        const { [q.event_id]: _, ...rest } = m;
        return rest;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="wingman-inbox-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-6 text-center" data-testid="wingman-inbox-empty">
        <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
          <Inbox className="w-6 h-6 text-slate-400" />
        </div>
        <p className="text-slate-300 font-medium mb-1">Inbox zero</p>
        <p className="text-slate-500 text-sm">When a buyer enquires about one of your listings you'll see it here.</p>
      </div>
    );
  }

  return (
    <>
      <ul className="px-4 py-4 space-y-3" data-testid="wingman-inbox-list">
      {items.map((q) => (
        <li
          key={q.event_id}
          className="rounded-2xl bg-white/5 border border-white/5 p-4"
          data-testid={`wingman-enquiry-${q.event_id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white truncate">{q.listing_title}</p>
              <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-400 flex-wrap">
                <Clock className="w-3 h-3" /> {formatRelative(q.created_at)}
                {q.plugin && <span className="px-1.5 py-0.5 rounded bg-white/5 uppercase tracking-wide">{q.plugin}</span>}
              </div>
            </div>
          </div>

          <div className="mt-3 text-sm text-slate-200">
            <p className="font-medium">{q.buyer_name || 'Anonymous buyer'}{q.buyer_company ? ` · ${q.buyer_company}` : ''}</p>
            {q.user_email && (
              <a
                href={`mailto:${q.user_email}?subject=${encodeURIComponent('Re: ' + q.listing_title)}&body=${encodeURIComponent('Hi,\n\nThanks for the enquiry — yes, still available. \n\nCheers')}`}
                className="inline-flex items-center gap-1 text-[#D4AF37] hover:underline mt-1"
                data-testid={`wingman-enquiry-email-${q.event_id}`}
              >
                <Mail className="w-3 h-3" /> {q.user_email}
              </a>
            )}
            {q.message && (
              <p className="mt-2 text-slate-300 text-[13px] leading-snug whitespace-pre-wrap">
                <MessageSquare className="w-3 h-3 inline -mt-0.5 mr-1 text-slate-500" />
                {q.message}
              </p>
            )}
          </div>

          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={() => setReplyOpen(q)}
              disabled={!q.inquiry_id}
              className="flex-1 text-center px-3 py-2 rounded-full bg-[#D4AF37] text-black text-sm font-semibold disabled:opacity-40"
              data-testid={`wingman-enquiry-reply-${q.event_id}`}
            >
              Reply
            </button>
            <button
              onClick={() => markReplied(q)}
              disabled={!q.inquiry_id || !!marking[q.event_id]}
              className="px-3 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-sm font-medium flex items-center gap-1 disabled:opacity-40"
              data-testid={`wingman-enquiry-mark-replied-${q.event_id}`}
            >
              {marking[q.event_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Done
            </button>
          </div>
        </li>
      ))}
      </ul>
      <ReplyDialog
        open={!!replyOpen}
        inquiry={replyOpen}
        onClose={() => setReplyOpen(null)}
        onReplied={() => {
          setItems((arr) => arr.filter((x) => x.event_id !== replyOpen.event_id));
          refreshMe?.();
        }}
        mobile
      />
    </>
  );
}
